import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Network, Loader2 } from 'lucide-react'
import { routingApi } from '../../../../services/routingApi'
import { staticNetworksApi } from '../../../../services/staticNetworksApi'

/**
 * Lista as redes remotas dos OUTROS peers da mesma VPN. Cada checkbox cria/remove
 * uma rota estática neste device, permitindo que ele acesse aquela rede via VPN.
 */
export default function RoutesSection({ device, deviceType }) {
  const { t } = useTranslation()
  const isRouter = deviceType === 'router'

  const [peersNets, setPeersNets] = useState([])
  const [staticNets, setStaticNets] = useState([])
  const [loading, setLoading] = useState(false)
  const [savingRoutes, setSavingRoutes] = useState({})

  const loadAll = useCallback(async () => {
    if (!device?.id || !device?.vpnNetworkId || !device?.vpnPeerId) return
    setLoading(true)
    try {
      const [peers, statics] = await Promise.all([
        routingApi.getPeersRemoteNetworks(device.vpnNetworkId, device.vpnPeerId),
        isRouter ? staticNetworksApi.getByRouter(device.id) : staticNetworksApi.getByHost(device.id),
      ])
      setPeersNets(peers || [])
      setStaticNets(statics || [])
    } catch (e) {
      console.error('routes load:', e)
    } finally {
      setLoading(false)
    }
  }, [device?.id, device?.vpnNetworkId, device?.vpnPeerId, isRouter])

  useEffect(() => { loadAll() }, [loadAll])

  const findStaticNet = (peer, rn) =>
    staticNets.find((sn) => sn.destination === rn.networkCidr && sn.description?.includes(peer.deviceName))

  const handleToggle = async (peer, rn) => {
    const key = `${peer.peerId}-${rn.id}`
    if (savingRoutes[key]) return
    setSavingRoutes((p) => ({ ...p, [key]: true }))
    try {
      const existing = findStaticNet(peer, rn)
      if (existing) {
        await staticNetworksApi.deleteGeneric(deviceType, device.id, existing.id)
      } else {
        await staticNetworksApi.createGeneric(deviceType, device.id, {
          destination: rn.networkCidr,
          gateway: peer.peerIp,
          distance: 1,
          scope: 30,
          routingTable: 'main',
          description: `Rota para ${peer.deviceName}`,
        })
      }
      try { await staticNetworksApi.applyPendingGeneric(deviceType, device.id) } catch {}
      await loadAll()
    } catch (e) {
      alert(e.response?.data?.message || t('config.routeAlterError'))
    } finally {
      setSavingRoutes((p) => ({ ...p, [key]: false }))
    }
  }

  if (!device?.vpnNetworkId) return null

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Network className="w-4 h-4 text-theme-primary" />
        <h3 className="text-sm font-semibold text-gray-900">{t('config.routesTitle')}</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">{t('config.routesSubtitle')}</p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('config.loading')}
        </div>
      ) : peersNets.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-2">{t('config.routesEmpty')}</p>
      ) : (
        <div className="space-y-1.5">
          {peersNets.map((pd) =>
            pd.remoteNetworks.map((rn) => {
              const active = !!findStaticNet(pd, rn)
              const key = `${pd.peerId}-${rn.id}`
              const saving = savingRoutes[key]
              return (
                <label key={key} className="flex items-center gap-3 p-2 bg-gray-50 border border-gray-200 rounded cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={active}
                    disabled={saving}
                    onChange={() => handleToggle(pd, rn)}
                    className="rounded border-gray-300 text-theme-primary focus:ring-theme-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-700">
                      {pd.deviceType === 'router' ? t('config.deviceRouter') : t('config.deviceHost')}{' '}
                      <span className="font-medium text-gray-900">{pd.deviceName}</span>
                    </span>
                    <span className="text-sm font-mono text-gray-600 ml-2">{rn.networkCidr}</span>
                    {rn.description && <span className="text-xs text-gray-400 ml-1">({rn.description})</span>}
                  </div>
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 shrink-0" />}
                </label>
              )
            })
          )}
        </div>
      )}
    </section>
  )
}
